import { createKeyValueStore } from '../../keyValueStore';
import { networkRequest } from '../adhoc';
import { constructIdempotencyKey } from '../logic';
import { RequestConfig, RequestPerformer } from '../types';
import { DUPLICATE_REQUEST_CANCEL_REASON } from './constants/duplicateRequestCancelReason';
import { CancelFn } from './types/CancelFn';

// only safe (non-mutating) HTTP methods participate in duplicate cancellation
const SAFE_METHODS = ['GET', 'HEAD'];

export const cancelableRequestFactory = (): RequestPerformer => {
  const inflightRequestsCache = createKeyValueStore<CancelFn>();

  const cancel = (idempotencyKey: string) => {
    const cancelFn = inflightRequestsCache.getValueForKey(idempotencyKey) as
      | CancelFn
      | undefined;
    cancelFn?.(DUPLICATE_REQUEST_CANCEL_REASON);
  };

  const cancelableRequest = async <ResponseType>(
    requestConfig: RequestConfig,
  ) => {
    // cancellation is a read optimisation: superseding a stale in-flight read
    // is safe, but aborting a write only stops the client listening while the
    // server may still apply it, and identical write payloads can be
    // legitimately distinct intents (e.g. two applicants saving the same
    // address). Writes bypass the registry entirely.
    if (!SAFE_METHODS.includes((requestConfig.method ?? 'GET').toUpperCase())) {
      return networkRequest<ResponseType>(requestConfig);
    }

    const idempotencyKey = constructIdempotencyKey(requestConfig);

    // cancel previous requests for the same key
    cancel(idempotencyKey);

    let handle: CancelFn | null = null;

    const register = (cancelFn: CancelFn) => {
      handle = cancelFn;
      inflightRequestsCache.setValueForKey(idempotencyKey, cancelFn);
    };

    const deregister = () => {
      if (inflightRequestsCache.getValueForKey(idempotencyKey) === handle) {
        inflightRequestsCache.deleteValueForKey(idempotencyKey);
      }
    };

    try {
      return await networkRequest<ResponseType>(requestConfig, register);
    } finally {
      deregister();
    }
  };

  return cancelableRequest;
};
